import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './YfDataStatus.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import Tooltip from '../../../components/molecules/Tooltip';
import MetricCell from './MetricCell';
import useElapsed from '../../hooks/useElapsed';
import {
    accountSide,
    AUDIT_METRICS,
    AUDIT_STATUS,
    formatCheckTime,
    formatDelta,
    formatElapsed,
    formatMetricValue,
    formatShortTime,
    hasDeltaChip,
    metricDisplayValue,
    hasAdnetSide,
    latestLoad,
    rowGroup,
    SLOW_PULL_MS,
    yfTranslationKey,
} from '../../yfDataStatusUtils';
import { auditAccountShape, auditDeltaShape, auditRowShape } from './propTypes';

function ConnectorMark({ connector }) {
    if (connector.logo_path) {
        return <img className={css.logo} src={connector.logo_path} alt="" />;
    }
    return <span className={css.logoStub}>{(connector.display_name || '?').charAt(0)}</span>;
}

ConnectorMark.propTypes = {
    connector: auditRowShape.isRequired,
};

function LoadedBy({ pullTag, loadedAt, timeZone, translate }) {
    return (
        <>
            {pullTag ? (
                <span className={css.loadTooltipTag}>{pullTag}</span>
            ) : (
                translate(yfTranslationKey('LOADED_BY_MANUAL'))
            )}
            {loadedAt &&
                translate(yfTranslationKey('LOADED_AT_TOOLTIP'), {
                    loadedAt: formatCheckTime(loadedAt, timeZone),
                    loadedAtUtc: formatCheckTime(loadedAt, 'UTC'),
                })}
        </>
    );
}

LoadedBy.propTypes = {
    pullTag: PropTypes.string,
    loadedAt: PropTypes.string,
    timeZone: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

function LoadTooltip({ connector, timeZone, translate }) {
    const accountLoads = (connector.loads || []).filter(load => load.account);

    if (accountLoads.length) {
        return (
            <>
                <div>{translate(yfTranslationKey('LOADED_BY_ACCOUNT_TOOLTIP'))}</div>
                <ul className={css.loadTooltipAccounts}>
                    {accountLoads.map((load, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                            <strong>{load.account}</strong>:{' '}
                            {load.pull_tag || load.loaded_at ? (
                                <LoadedBy
                                    pullTag={load.pull_tag}
                                    loadedAt={load.loaded_at}
                                    timeZone={timeZone}
                                    translate={translate}
                                />
                            ) : (
                                translate(yfTranslationKey('ACCOUNT_NOT_LOADED'))
                            )}
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    const load = latestLoad(connector);
    if (!load.pull_tag) {
        return translate(yfTranslationKey('LOADED_BY_MANUAL_TOOLTIP'));
    }

    return (
        <>
            {translate(yfTranslationKey('LOADED_BY_TOOLTIP'))}{' '}
            <LoadedBy pullTag={load.pull_tag} loadedAt={load.loaded_at} timeZone={timeZone} translate={translate} />
        </>
    );
}

LoadTooltip.propTypes = {
    connector: auditRowShape.isRequired,
    timeZone: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

function AccountMetric({ delta, comparable, translate }) {
    const { label, tone } = formatDelta(delta);
    const differs = comparable && hasDeltaChip(delta);

    return (
        <div className={css.accountMetric}>
            <div className={css.accountMetricLine}>
                {differs && <span className={`${css.chip} ${css[`chip_${tone}`]}`}>{label}</span>}
                <span className={css.metricValue}>{metricDisplayValue(delta)}</span>
            </div>
            {differs && delta.adnet_value !== null && delta.adnet_value !== undefined && (
                <div className={css.accountAdnetValue}>
                    {translate(yfTranslationKey('ADNET_VALUE_INLINE'), { value: formatMetricValue(delta.adnet_value) })}
                </div>
            )}
        </div>
    );
}

AccountMetric.propTypes = {
    delta: auditDeltaShape.isRequired,
    comparable: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

const accountLoadShape = PropTypes.shape({
    pull_tag: PropTypes.string,
    loaded_at: PropTypes.string,
});

function AccountLoad({ load = null, timeZone, translate }) {
    if (!load) {
        return null;
    }
    if (!load.loaded_at) {
        return (
            <>
                <span className={css.timeLabel}>{translate(yfTranslationKey('LOADED_LABEL'))}</span>
                <span className={css.timeMuted}>{translate(yfTranslationKey('ACCOUNT_NOT_LOADED'))}</span>
            </>
        );
    }
    return (
        <>
            <span className={css.timeLabel}>{translate(yfTranslationKey('LOADED_LABEL'))}</span>
            <span className={css.timeValue}>
                <span className={css.timeText} title={formatCheckTime(load.loaded_at, timeZone)}>
                    {formatShortTime(load.loaded_at, timeZone)}
                </span>
            </span>
            <span className={css.accountLoadTask} title={load.pull_tag}>
                {load.pull_tag || translate(yfTranslationKey('LOADED_BY_MANUAL'))}
            </span>
        </>
    );
}

AccountLoad.propTypes = {
    load: accountLoadShape,
    timeZone: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

const peerLabel = peer => `${peer.label} (#${peer.uan_id})`;

function SharedAccountMark({ title }) {
    return (
        <Tooltip title={title} wrapperClass={css.sharedMark}>
            <Icon
                name="alert-triangle"
                type={IconTypes.TABLER}
                size={IconSizes.XX_SMALL}
                variant={IconVariants.WARNING}
                interactive={false}
            />
        </Tooltip>
    );
}

SharedAccountMark.propTypes = {
    title: PropTypes.string.isRequired,
};

function AccountRow({ account, load = null, comparable, timeZone, translate }) {
    const label = account.name || account.account_id || translate(yfTranslationKey('NO_ACCOUNT_ID'));
    const side = accountSide(account.deltas);
    const loadedUnder = account.loaded_under;
    const sharedWith = account.shared_with || [];

    return (
        <div className={`${css.row} ${css.accountRow}`}>
            <div className={css.accountNameCell}>
                <div className={css.accountNameLine}>
                    <div className={css.accountName} title={label}>
                        {label}
                    </div>
                    {sharedWith.length > 0 && (
                        <SharedAccountMark
                            title={translate(
                                yfTranslationKey(loadedUnder ? 'ACCOUNT_LOADED_UNDER' : 'ACCOUNT_SHARED'),
                                {
                                    connectors: sharedWith.map(peerLabel).join(', '),
                                    connector: loadedUnder ? peerLabel(loadedUnder) : '',
                                }
                            )}
                        />
                    )}
                </div>
                {account.name && (
                    <div className={css.subLabel} title={account.account_id}>
                        {account.account_id}
                    </div>
                )}
                {comparable && side && !loadedUnder && (
                    <span className={css.accountSideTag}>{translate(yfTranslationKey(`ACCOUNT_SIDE.${side}`))}</span>
                )}
            </div>
            {AUDIT_METRICS.map(metric =>
                loadedUnder ? (
                    <div key={metric} className={css.accountMetric}>
                        <span className={css.metricValue}>—</span>
                    </div>
                ) : (
                    <AccountMetric
                        key={metric}
                        delta={account.deltas[metric]}
                        comparable={comparable}
                        translate={translate}
                    />
                )
            )}
            <div className={css.timesCell}>
                <AccountLoad load={load} timeZone={timeZone} translate={translate} />
            </div>
        </div>
    );
}

AccountRow.propTypes = {
    account: auditAccountShape.isRequired,
    load: accountLoadShape,
    comparable: PropTypes.bool.isRequired,
    timeZone: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

function ConnectorRow({
    connector,
    isCalculating,
    isQueued = false,
    startedAt = null,
    error = null,
    onCalculate,
    timeZone,
    translate,
    adminMode = false,
}) {
    const elapsedMs = useElapsed(isCalculating ? startedAt : null);
    const [showAccounts, setShowAccounts] = useState(false);

    const group = rowGroup(connector);
    const isBusy = isCalculating || isQueued;
    const hasError = !!error && !isCalculating;
    const comparable = hasAdnetSide(connector.deltas);
    const lastLoad = latestLoad(connector);
    const isUnavailable = connector.status === AUDIT_STATUS.UNAVAILABLE;
    const accounts = connector.accounts || [];
    const sharedCount = accounts.filter(account => (account.shared_with || []).length > 0).length;
    const isLoadedElsewhere = accounts.length > 0 && accounts.every(account => account.loaded_under);
    const isVerifiedEmpty = !comparable && !isLoadedElsewhere && connector.status === AUDIT_STATUS.SYNCED;

    return (
        <>
            <div className={`${css.row} ${css[`edge_${group}`]} ${isBusy ? css.rowBusy : ''}`}>
                <div className={css.nameCell}>
                    <ConnectorMark connector={connector} />
                    <div className={css.nameText}>
                        <div className={css.connectorName} title={connector.display_name}>
                            {connector.display_name}
                            {adminMode && <span className={css.uanId}>({connector.uan_id})</span>}
                        </div>
                        <div className={css.subLabel} title={connector.label}>
                            {connector.label}
                        </div>
                        {(accounts.length > 0 || connector.audit_uan_id) && (
                            <div className={css.metaLine}>
                                {accounts.length > 0 && (
                                    <button
                                        type="button"
                                        className={css.accountsToggle}
                                        aria-expanded={showAccounts}
                                        onClick={() => setShowAccounts(shown => !shown)}
                                    >
                                        <Icon
                                            name={showAccounts ? 'chevron-down' : 'chevron-right'}
                                            size={IconSizes.XX_SMALL}
                                            interactive={false}
                                        />
                                        {translate(
                                            yfTranslationKey(
                                                accounts.length === 1 ? 'ACCOUNTS_TOGGLE_ONE' : 'ACCOUNTS_TOGGLE'
                                            ),
                                            { count: accounts.length }
                                        )}
                                    </button>
                                )}
                                {sharedCount > 0 && (
                                    <SharedAccountMark
                                        title={translate(
                                            yfTranslationKey(
                                                sharedCount === 1 ? 'SHARED_ACCOUNTS_ONE' : 'SHARED_ACCOUNTS_MANY'
                                            ),
                                            { count: sharedCount }
                                        )}
                                    />
                                )}
                                {connector.audit_uan_id && (
                                    <span className={css.auditUan}>
                                        {translate(yfTranslationKey('AUDIT_UAN'), { id: connector.audit_uan_id })}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {hasError && (
                    <div className={css.spanningCell} title={error}>
                        <Icon
                            name="alert-triangle"
                            type={IconTypes.TABLER}
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.ERROR}
                            interactive={false}
                        />
                        <span className={`${css.spanningText} ${css.spanningError}`}>{error}</span>
                    </div>
                )}

                {!hasError && isUnavailable && (
                    <div className={css.spanningCell}>
                        <Icon
                            name="alert-triangle"
                            type={IconTypes.TABLER}
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.WARNING}
                            interactive={false}
                        />
                        <span className={`${css.spanningText} ${css.spanningWarning}`}>
                            {translate(yfTranslationKey('UNAVAILABLE_EXPLANATION'))}
                        </span>
                    </div>
                )}

                {!hasError && isLoadedElsewhere && (
                    <div className={css.spanningCell}>
                        <span className={css.spanningText}>
                            {translate(yfTranslationKey('ALL_ACCOUNTS_LOADED_ELSEWHERE'))}
                        </span>
                    </div>
                )}

                {!hasError && isVerifiedEmpty && (
                    <div className={css.spanningCell}>
                        <span className={css.spanningText}>{translate(yfTranslationKey('EMPTY_DAY_EXPLANATION'))}</span>
                    </div>
                )}

                {!hasError &&
                    !isVerifiedEmpty &&
                    !isLoadedElsewhere &&
                    !isUnavailable &&
                    AUDIT_METRICS.map(metric => (
                        <MetricCell
                            key={metric}
                            delta={connector.deltas[metric]}
                            comparable={comparable}
                            translate={translate}
                        />
                    ))}

                <div className={css.timesCell}>
                    <span className={css.timeLabel}>{translate(yfTranslationKey('LOADED_LABEL'))}</span>
                    <span className={css.timeValue}>
                        <Tooltip
                            title={<LoadTooltip connector={connector} timeZone={timeZone} translate={translate} />}
                            wrapperClass={css.timeTooltip}
                        >
                            <span className={lastLoad.loaded_at ? css.timeText : css.timeMuted}>
                                {formatShortTime(lastLoad.loaded_at, timeZone) ||
                                    translate(yfTranslationKey('LOADED_BY_MANUAL'))}
                            </span>
                        </Tooltip>
                        {connector.loaded_after_check && (
                            <Tooltip
                                title={translate(yfTranslationKey('LOADED_AFTER_CHECK_TOOLTIP'))}
                                wrapperClass={css.reloadMark}
                            >
                                <Icon
                                    name="history"
                                    type={IconTypes.TABLER}
                                    size={IconSizes.XX_SMALL}
                                    variant={IconVariants.ACTIVE}
                                    interactive={false}
                                />
                            </Tooltip>
                        )}
                    </span>

                    <span className={css.timeLabel}>{translate(yfTranslationKey('CHECKED_LABEL'))}</span>
                    <span className={css.timeValue}>
                        {isBusy && (
                            <span className={css.checking}>
                                <span className={css.checkingSpinner} />
                                {isCalculating
                                    ? formatElapsed(elapsedMs)
                                    : translate(yfTranslationKey('STATUSES.QUEUED'))}
                            </span>
                        )}
                        {!isBusy && connector.pulled_at && (
                            <Tooltip
                                title={`${formatCheckTime(connector.pulled_at, timeZone)} (${formatCheckTime(
                                    connector.pulled_at,
                                    'UTC'
                                )})`}
                                wrapperClass={css.timeTooltip}
                            >
                                <span className={css.timeText}>{formatShortTime(connector.pulled_at, timeZone)}</span>
                            </Tooltip>
                        )}
                        {!isBusy && !connector.pulled_at && (
                            <span className={css.timeMuted}>{translate(yfTranslationKey('NOT_CHECKED'))}</span>
                        )}
                    </span>
                </div>

                <div className={css.actionCell}>
                    {connector.can_calculate && !isBusy && (
                        <button
                            type="button"
                            className={css.checkButton}
                            title={translate(yfTranslationKey('CALCULATE_ROW_TOOLTIP'))}
                            aria-label={translate(yfTranslationKey('CALCULATE_ROW_BUTTON'))}
                            onClick={() => onCalculate(connector.uan_id, true)}
                        >
                            <Icon
                                name="refresh"
                                type={IconTypes.TABLER}
                                size={IconSizes.XX_SMALL}
                                variant={IconVariants.ACTIVE}
                            />
                        </button>
                    )}
                </div>

                {elapsedMs > SLOW_PULL_MS && (
                    <div className={css.rowHint}>{translate(yfTranslationKey('SLOW_PULL_HINT'))}</div>
                )}
            </div>
            {showAccounts &&
                accounts.map(account => (
                    <AccountRow
                        key={account.account_id}
                        account={account}
                        load={(connector.loads || []).find(entry => entry.account_id === account.account_id)}
                        comparable={comparable}
                        timeZone={timeZone}
                        translate={translate}
                    />
                ))}
        </>
    );
}

ConnectorRow.propTypes = {
    connector: auditRowShape.isRequired,
    isCalculating: PropTypes.bool.isRequired,
    isQueued: PropTypes.bool,
    startedAt: PropTypes.number,
    error: PropTypes.string,
    onCalculate: PropTypes.func.isRequired,
    timeZone: PropTypes.string,
    translate: PropTypes.func.isRequired,
    adminMode: PropTypes.bool,
};

export default ConnectorRow;
